/* eslint-disable global-require */
import React, { useState, useContext } from 'react';
import { PageContext } from '@/context/PageContext';
import { Helmet } from 'react-helmet';

import { ImportantInformation } from '@latitude/important-information';
import { BUTTON_STYLE, COLOR } from '@latitude/core/utils/constants';
import Section from '@latitude/section';
import ListNumbered from '@latitude/list-numbered/ListNumbered';
import { TabNavigation } from '@latitude/tab-navigation';
import { Link } from '@latitude/link';

import Layout from '@/components/layout';
import PageHeaderSection from '@/components/PageLayout/PageHeader';

import ExpediaWotifSection from './_expedia-wotif';
import imgHeroTravelPartners from './images/lframe-travel-partners.webp';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';

const STATIC_HOW_TO_CLAIM_TABS = [
  {
    id: '#expedia',
    label: 'Expedia',
    steps: [
      "<span>Click the 'Book now' button below.</span>",
      "<span>Search an eligible 'Pay now' hotel.</span>",
      "<span>Under the link 'Use a coupon, credit or promotion code', enter the Expedia coupon code and click the 'Apply' button. The coupon code can be found in the Expedia tile in the Offers tab of the Latitude App or the Latitude Service Centre.</span>",
      '<span>If your booking is eligible, you will see the discount applied. The offer applies to the first room in the multi-room bookings.</span>',
      '<span>You must use your Latitude 28° Global Platinum Mastercard, Latitude GO Mastercard, Latitude GO Platinum Mastercard or Latitude Gem Visa credit card to pay for the booking.</span>'
    ],
    cta: {
      href: 'https://www.expedia.com.au/lp/b/latitudecard',
      text: 'Book Now',
      trackEventData: {
        category: 'cta',
        action: 'external-link',
        location: 'How to claim Expedia'
      }
    }
  },
  {
    id: '#wotif',
    label: 'Wotif',
    steps: [
      "<span>Click the 'Book now' button below.</span>",
      "<span>Search an eligible 'Pay now' hotel.</span>",
      "<span>Under the link 'Use a coupon, credit or promotion code', enter the Wotif coupon code and click the 'Apply' button. The coupon code can be found in the Wotif tile in the Offers tab of the Latitude App or the Latitude Service Centre.</span>",
      '<span>If your booking is eligible, you will see the discount applied. The offer applies to the first room in the multi-room bookings.</span>',
      '<span>You must use your Latitude 28° Global Platinum Mastercard, Latitude GO Mastercard, Latitude GO Platinum Mastercard or Latitude Gem Visa credit card to pay for the booking.</span>'
    ],
    cta: {
      href: 'https://www.wotif.com/?clickref=1011lyjvgQZG&affcid=wotif-AU.network.phg.latitudefinancial.hotels&afflid=1011lyjvgQZG&ref_id=1011lyjvgQZG&my_ad=wotif-AU.network.phg.latitudefinancial.hotels&my_publisher=latitudefinancial',
      text: 'Book Now',
      trackEventData: {
        category: 'cta',
        action: 'external-link',
        location: 'How to claim Wotif'
      }
    }
  }
];

function TravelPartnersPage({ location }) {
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || {};
  const importantInfoData = state?.importantInfoData?.[0];
  const howToClaimData = state?.travelHowToClaimData?.[0];
  const howToClaimTabs = howToClaimData?.tabs?.length
    ? howToClaimData.tabs
    : STATIC_HOW_TO_CLAIM_TABS;
  const [activeLinkId, setActiveLinkId] = useState(howToClaimTabs[0]?.id);
  const activeHowToClaimTab =
    howToClaimTabs.find(tab => tab.id === activeLinkId) || howToClaimTabs[0];

  function onTabClickHandler(linkId) {
    setActiveLinkId(linkId);
  }

  return (
    <Layout location={location}>
      <Helmet>
        <link
          rel="canonical"
          href="https://www.latitudefinancial.com.au/travelpartners/"
        />
        <title>
          Travel Partners | Latitude 28&deg; Global Platinum Mastercard |
          International Credit Card
        </title>
      </Helmet>
      <main className="navigation-spacer">
        {state?.heroBannerData?.[0] ? (
          <HeroV2 {...state.heroBannerData[0]} />
        ) : (
          <PageHeaderSection
            title={
              <>
                Escape the grind.
                <br />
                Escape paying full price
              </>
            }
            subTitle={<>Enjoy great savings with our travel partners.</>}
            pageImage={imgHeroTravelPartners}
            pageImageAlt="Travel Partners"
          />
        )}

        <ExpediaWotifSection
          anchor="expedia-wotif"
          heading="Get more from your getaway"
          data={state?.cardCollectionData?.[0]}
        />

        <Section
          id="how-to-claim"
          heading={howToClaimData?.title || 'How to claim'}
          css={`
            text-align: center;
            background-color: ${COLOR.WHITE};
            padding-bottom: 0;
          `}
        >
          <TabNavigation
            basepath="/travelpartners/"
            data={howToClaimTabs.map(({ id, label }) => ({ id, label }))}
            defaultActiveTabId={howToClaimTabs[0]?.id}
            onTabClick={onTabClickHandler}
          />
        </Section>

        <div
          className="row"
          css={{ backgroundColor: COLOR.BLUE_BABY, padding: '20px 10px' }}
        >
          <div className="col-md-7 offset-md-3">
            {activeHowToClaimTab && (
              <>
                <ListNumbered
                  separator="none"
                  viewStyle="compact"
                  data={activeHowToClaimTab.steps}
                />
                {activeHowToClaimTab.cta?.href && (
                  <Link
                    href={activeHowToClaimTab.cta.href}
                    button={BUTTON_STYLE.SECONDARY}
                    css={{
                      margin: '20pt auto 0 auto',
                      width: 'fit-content'
                    }}
                    trackId={activeLinkId}
                    trackEventData={activeHowToClaimTab.cta.trackEventData}
                  >
                    {activeHowToClaimTab.cta.text}
                  </Link>
                )}
              </>
            )}
          </div>
          <div css={{ width: '100%' }}></div>
        </div>

        {/* <WhyChooseSection
          anchor="why-choose"
          heading="Don't forget all the other amazing features to enjoy!"
        />

        <DontHaveACardSection anchor="dont-have-card" />

        <HaveAQuestionSection
          anchor="have-question"
          heading="Have a question?"
        /> */}

        <ImportantInformation
          data={
            importantInfoData?.data || {
              content: {
                importantInformationSectionOne: [
                  'Mastercard and circles design are registered trademarks of Mastecard International Incorporated.'
                ]
              }
            }
          }
          sectionOneColummClasses={
            importantInfoData?.sectionOneColummClasses || 'offset-lg-3'
          }
          sectionTwoColummClasses={importantInfoData?.sectionTwoColummClasses}
        />
      </main>
    </Layout>
  );
}

export default TravelPartnersPage;
